/**
 * Simplified API Helper - Clean CRUD operations
 * 
 * Usage Examples:
 * 
 * // GET request
 * import { get } from './apiHelperNew';
 * const response = await get('/api/users');
 * const responseWithParams = await get('/api/users', { page: 1, limit: 10 });
 * 
 * // POST request
 * import { post } from './apiHelperNew';
 * const response = await post('/api/users', { name: 'John', email: 'john@example.com' });
 * 
 * // POST with file upload
 * const formData = new FormData();
 * formData.append('file', file);
 * const response = await post('/api/upload', formData, { isFile: true });
 * 
 * // PUT request
 * import { put } from './apiHelperNew';
 * const response = await put('/api/users/123', { name: 'Jane' });
 * 
 * // PUT with query params
 * const response = await put('/api/users/123', { name: 'Jane' }, { params: { notify: true } });
 * 
 * // DELETE request
 * import { del } from './apiHelperNew';
 * const response = await del('/api/users/123');
 * 
 * // Error handling with returnError option
 * const response = await post('/api/users', data, { returnError: true });
 * if (response.status === 'error') {
 *   console.error(response.propererror);
 * }
 * 
 * // Request without token
 * const response = await get('/api/public/data', null, { includeToken: false });
 * 
 * Migration from old apiHelper:
 * - getRequestData(url) → get(url)
 * - createRequestData(data, url) → post(url, data)
 * - updateRequestData(data, url) → put(url, data)
 * - requestDeleteApiCall(url) → del(url)
 * - createRequestData(data, url, true) → post(url, data, { isFile: true })
 */

import qs from "qs";
import axiosInstance from "../api/axiosInstance";

/**
 * Get authorization headers
 * @param {boolean} isFile - Whether this is a file upload request
 * @param {boolean} includeToken - Whether to include auth token (default: true)
 * @returns {Object} Headers object
 */
const getHeaders = (isFile = false, includeToken = true) => {
  const headers = {
    'Accept': 'application/json',
    'Content-Type': isFile ? 'multipart/form-data' : 'application/json',
  };

  if (includeToken) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return { headers };
};

/**
 * Base API request function
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} url - API endpoint URL
 * @param {Object|null} data - Request body data (null for GET/DELETE)
 * @param {Object} options - Additional options (isFile, includeToken, params, responseType)
 * @returns {Promise} Axios response or error object
 */
const apiRequest = async (method, url, data = null, options = {}) => {
  const {
    isFile = false,
    includeToken = true,
    params = null,
    responseType = 'json',
    returnError = false
  } = options;

  const config = {
    method,
    url,
    ...getHeaders(isFile, includeToken),
    responseType,
  };

  if (data !== null) {
    config.data = data;
  }

  if (params) {
    config.params = params;
    config.paramsSerializer = (p) => qs.stringify(p, { arrayFormat: 'repeat' });
  }

  try {
    const response = await axiosInstance(config);
    return response;
  } catch (error) {
    if (returnError) {
      return { propererror: error?.response, status: 'error' };
    }
    return error;
  }
};

/**
 * GET request
 * @param {string} url - API endpoint URL
 * @param {Object} params - Query parameters (optional)
 * @param {Object} options - Additional options (includeToken, returnError)
 * @returns {Promise} Axios response or error object
 */
export const get = (url, params = null, options = {}) => {
  return apiRequest('GET', url, null, { ...options, params });
};

/**
 * POST request
 * @param {string} url - API endpoint URL
 * @param {Object} data - Request body data
 * @param {Object} options - Additional options (isFile, includeToken, returnError)
 * @returns {Promise} Axios response or error object
 */
export const post = (url, data, options = {}) => {
  return apiRequest('POST', url, data, options);
};

/**
 * PUT request
 * @param {string} url - API endpoint URL
 * @param {Object} data - Request body data
 * @param {Object} options - Additional options (isFile, includeToken, params, returnError)
 * @returns {Promise} Axios response or error object
 */
export const put = (url, data, options = {}) => {
  return apiRequest('PUT', url, data, options);
};

/**
 * DELETE request
 * @param {string} url - API endpoint URL
 * @param {Object} options - Additional options (includeToken, returnError)
 * @returns {Promise} Axios response or error object
 */
export const del = (url, options = {}) => {
  return apiRequest('DELETE', url, null, options);
};

/**
 * Download document as blob
 * @param {string} documentId - Document ID
 * @returns {Promise} Axios response with blob data
 */
export const getDocumentBlob = (documentId,url) => {
  const docUrl = url || `${process.env.REACT_APP_BASE_URL}documents/download/${documentId}`
  return axiosInstance.get(
    docUrl,
    {
      responseType: 'blob',
      ...getHeaders(false, true),
    }
  );
};

/**
 * Download document and trigger browser download
 * @param {string} documentId - Document ID
 * @returns {Promise<boolean>} Success status
 */
export const downloadDocument = async (documentId,url) => {
  try {
    const response = await getDocumentBlob(documentId,url);
    const disposition = response.headers['content-disposition'];
    let filename = `download-${Date.now()}`;

    if (disposition) {
      const utf8Match = disposition.match(/filename\*=UTF-8''(.+)/);
      if (utf8Match?.[1]) {
        filename = decodeURIComponent(utf8Match[1]);
      } else {
        const asciiMatch = disposition.match(/filename="([^"]+)"/);
        if (asciiMatch?.[1]) {
          filename = asciiMatch[1];
        }
      }
    }

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

    return true;
  } catch (error) {
    console.error("Download failed", error);
    return false;
  }
};

/**
 * Get document as blob URL for preview
 * @param {string} documentId - Document ID
 * @returns {Promise<string|null>} Blob URL or null
 */
export const getDocumentAsBlobUrl = async (documentId) => {
  if (!documentId) return null;
  try {
    const response = await getDocumentBlob(documentId);
    const blob = response.data;
    const contentType = response.headers['content-type'] || blob.type || 'application/octet-stream';
    const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
    return blobUrl;
  } catch (err) {
    return null;
  }
};

// Export default object with all methods for convenience
export default {
  get,
  post,
  put,
  delete: del,
  getDocumentBlob,
  downloadDocument,
  getDocumentAsBlobUrl,
};

